import { serverDomain } from '../pages/config';

export const PROFILE_ICON_STORAGE_KEY = 'appstar_user_profile_icon';
export const PROFILE_ICON_HASH_STORAGE_KEY = 'appstar_profile_icon_hash';
export const PROFILE_STATE_UPDATED_EVENT = 'appstar-profile-state-updated';

const PROFILE_ICON_GROUPS = [
  { letters: 'ABC', background: '#ef4444' },
  { letters: 'DEF', background: '#f97316' },
  { letters: 'GHI', background: '#eab308' },
  { letters: 'JKL', background: '#22c55e' },
  { letters: 'MNO', background: '#06b6d4' },
  { letters: 'PQR', background: '#3b82f6' },
  { letters: 'STUV', background: '#8b5cf6' },
  { letters: 'WXYZ', background: '#ffffff' }
];

const DEFAULT_PROFILE_ICON = {
  kind: 'initials',
  initials: '??',
  background: '#ffffff',
  textColor: '#000000',
  fullName: '',
  hash: null,
  src: ''
};

const PROFILE_FIELD_KEYS = {
  username: 'appstar_username',
  firstName: 'appstar_first_name',
  lastName: 'appstar_last_name',
  email: 'appstar_email',
  profileIconHash: PROFILE_ICON_HASH_STORAGE_KEY
};

export const getProfileFullName = ({ firstName = '', lastName = '' }) => {
  return [String(firstName || '').trim(), String(lastName || '').trim()].filter(Boolean).join(' ');
};

export const buildProfileImageUrl = (profileIconHash) => {
  const hash = String(profileIconHash || '').trim();
  if (!hash) return '';
  return `${serverDomain}profile-images/${encodeURIComponent(hash)}.jpg`;
};

const getLeadingCharacter = (value) => {
  const normalizedValue = String(value || '').trim();
  const match = normalizedValue.match(/[A-Za-z0-9]/);
  return match ? match[0].toUpperCase() : '';
};

const getUsernameFallbackInitials = (username) => {
  const sanitized = String(username || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (!sanitized) return '';
  return sanitized.slice(0, 2).padEnd(2, sanitized.charAt(0));
};

export const getProfileInitials = ({ firstName = '', lastName = '', username = '' }) => {
  const firstInitial = getLeadingCharacter(firstName);
  const lastInitial = getLeadingCharacter(lastName);

  if (firstInitial && lastInitial) return `${firstInitial}${lastInitial}`;
  if (firstInitial) return `${firstInitial}${firstInitial}`;
  if (lastInitial) return `${lastInitial}${lastInitial}`;

  return getUsernameFallbackInitials(username) || DEFAULT_PROFILE_ICON.initials;
};

export const getProfileIconBackground = (initials) => {
  const lead = getLeadingCharacter(initials);
  if (!lead || !/[A-Z]/.test(lead)) return DEFAULT_PROFILE_ICON.background;

  const match = PROFILE_ICON_GROUPS.find((group) => group.letters.includes(lead));
  return match ? match.background : DEFAULT_PROFILE_ICON.background;
};

export const buildProfileIconPayload = ({ firstName = '', lastName = '', username = '', profileIconHash = '' }) => {
  const initials = getProfileInitials({ firstName, lastName, username });
  const fullName = getProfileFullName({ firstName, lastName });
  const hash = String(profileIconHash || '').trim();

  if (hash) {
    return {
      kind: 'image',
      hash,
      src: buildProfileImageUrl(hash),
      initials,
      background: getProfileIconBackground(initials),
      textColor: '#000000',
      fullName
    };
  }

  return {
    kind: 'initials',
    initials,
    background: getProfileIconBackground(initials),
    textColor: '#000000',
    fullName,
    hash: null,
    src: ''
  };
};

export const parseProfileIconPayload = (value) => {
  if (!value) return null;

  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

export const getStoredProfileState = () => {
  if (typeof window === 'undefined') {
    return {
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      profileIconHash: ''
    };
  }

  return {
    username: localStorage.getItem(PROFILE_FIELD_KEYS.username) || '',
    firstName: localStorage.getItem(PROFILE_FIELD_KEYS.firstName) || '',
    lastName: localStorage.getItem(PROFILE_FIELD_KEYS.lastName) || '',
    email: localStorage.getItem(PROFILE_FIELD_KEYS.email) || '',
    profileIconHash: localStorage.getItem(PROFILE_FIELD_KEYS.profileIconHash) || ''
  };
};

const writeStorageValue = (key, value) => {
  if (value === undefined) return;
  if (value === null) {
    localStorage.removeItem(key);
    return;
  }

  const normalized = String(value).trim();
  if (normalized) {
    localStorage.setItem(key, normalized);
  } else {
    localStorage.removeItem(key);
  }
};

export const emitProfileStateUpdated = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PROFILE_STATE_UPDATED_EVENT));
};

export const persistProfileState = (user = {}) => {
  if (typeof window === 'undefined') return null;

  const current = getStoredProfileState();
  const nextState = {
    username: user.username ?? current.username,
    firstName: user.firstName ?? user.first_name ?? current.firstName,
    lastName: user.lastName ?? user.last_name ?? current.lastName,
    email: user.email ?? current.email,
    profileIconHash: user.profileIconHash ?? user.profile_icon_hash ?? current.profileIconHash
  };

  writeStorageValue(PROFILE_FIELD_KEYS.username, nextState.username);
  writeStorageValue(PROFILE_FIELD_KEYS.firstName, nextState.firstName);
  writeStorageValue(PROFILE_FIELD_KEYS.lastName, nextState.lastName);
  writeStorageValue(PROFILE_FIELD_KEYS.email, nextState.email);
  writeStorageValue(PROFILE_FIELD_KEYS.profileIconHash, nextState.profileIconHash);

  const payload = buildProfileIconPayload(nextState);
  localStorage.setItem(PROFILE_ICON_STORAGE_KEY, JSON.stringify(payload));
  emitProfileStateUpdated();
  return payload;
};

export const clearStoredProfileState = () => {
  if (typeof window === 'undefined') return;

  localStorage.removeItem(PROFILE_FIELD_KEYS.username);
  localStorage.removeItem(PROFILE_FIELD_KEYS.firstName);
  localStorage.removeItem(PROFILE_FIELD_KEYS.lastName);
  localStorage.removeItem(PROFILE_FIELD_KEYS.email);
  localStorage.removeItem(PROFILE_FIELD_KEYS.profileIconHash);
  localStorage.removeItem(PROFILE_ICON_STORAGE_KEY);
  emitProfileStateUpdated();
};

export const syncStoredProfileIcon = (user = {}) => {
  if (typeof window === 'undefined') return null;

  const current = getStoredProfileState();
  const nextState = {
    username: user.username ?? current.username,
    firstName: user.firstName ?? user.first_name ?? current.firstName,
    lastName: user.lastName ?? user.last_name ?? current.lastName,
    profileIconHash: user.profileIconHash ?? user.profile_icon_hash ?? current.profileIconHash
  };

  const payload = buildProfileIconPayload(nextState);
  localStorage.setItem(PROFILE_ICON_STORAGE_KEY, JSON.stringify(payload));
  return payload;
};

export const ensureStoredProfileIcon = (user = {}) => {
  if (typeof window === 'undefined') return null;

  const existing = parseProfileIconPayload(localStorage.getItem(PROFILE_ICON_STORAGE_KEY));
  if (existing) {
    const currentHash = localStorage.getItem(PROFILE_ICON_HASH_STORAGE_KEY) || '';
    const currentState = getStoredProfileState();
    const expectedPayload = buildProfileIconPayload({
      username: user.username ?? currentState.username,
      firstName: user.firstName ?? user.first_name ?? currentState.firstName,
      lastName: user.lastName ?? user.last_name ?? currentState.lastName,
      profileIconHash: user.profileIconHash ?? user.profile_icon_hash ?? currentHash
    });

    if (JSON.stringify(existing) === JSON.stringify(expectedPayload)) return existing;
  }

  return syncStoredProfileIcon(user);
};